/**
 * Pure model for the "Späteres Datum wählen" month calendar (PLA-880).
 *
 * Open/closed comes from opening hours only (the open-date list). Free-time
 * counts are shown ONLY for days whose slots are already cached; the calendar
 * never triggers a fetch of its own.
 */
import dayjs, { type Dayjs } from "dayjs"

import "dayjs/locale/de"

import { fmt, type DayResult } from "./rescheduleSlotModel"

export const WEEKDAY_HEADERS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const

export interface CalendarCell {
  date: string
  day: number
  /** Selectable: inside opening hours and the bookable horizon. */
  open: boolean
  /** Free times when the day is cached, else null (no count shown). */
  count: number | null
}

export interface CalendarWeek {
  /** ISO calendar week, e.g. 44 for "KW 44". */
  kw: number
  /** Always 7 entries Mo..So; null pads the month edges. */
  cells: (CalendarCell | null)[]
}

export interface CalendarMonthView {
  /** First of the shown month, YYYY-MM-DD. */
  month: string
  title: string
  weeks: CalendarWeek[]
  canPrev: boolean
  canNext: boolean
  /** The legend is only meaningful if at least one count is visible. */
  showLegend: boolean
}

const DAY_MS = 86_400_000

/** ISO-8601 week number (weeks start Monday, week 1 holds the first Thursday). */
export function isoWeekNumber(d: Dayjs): number {
  const date = new Date(Date.UTC(d.year(), d.month(), d.date()))
  const weekday = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - weekday)
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1)
  return Math.ceil(((date.getTime() - yearStart) / DAY_MS + 1) / 7)
}

export const monthStartOf = (d: Dayjs | string): string => dayjs(d).format("YYYY-MM-01")

/** Navigable months: the current month through the month of the last open date. */
export function calendarBounds(openDates: string[], now: Dayjs): { min: string; max: string } {
  const min = monthStartOf(now)
  const last = openDates[openDates.length - 1]
  return { min, max: last ? monthStartOf(last) : min }
}

export function shiftMonth(month: string, delta: -1 | 1, bounds: { min: string; max: string }) {
  const next = monthStartOf(dayjs(month).add(delta, "month"))
  return next < bounds.min || next > bounds.max ? month : next
}

function freeCount(result: DayResult, taken: string[] = []): number | null {
  if (result === undefined || result === "loading" || result === "error") return null
  return result.times.filter((t) => !taken.includes(t)).length
}

export interface BuildCalendarInput {
  month: string
  openDates: string[]
  results: Record<string, DayResult>
  taken?: Record<string, string[]>
  now: Dayjs
}

export function buildCalendarMonth({
  month,
  openDates,
  results,
  taken = {},
  now,
}: BuildCalendarInput): CalendarMonthView {
  const first = dayjs(month).startOf("month")
  const open = new Set(openDates)
  const lead = (first.day() + 6) % 7
  const cells: (CalendarCell | null)[] = Array(lead).fill(null)
  for (let n = 1; n <= first.daysInMonth(); n++) {
    const date = first.date(n).format("YYYY-MM-DD")
    const count = open.has(date) ? freeCount(results[date], taken[date]) : null
    // A cached day without free times is as good as closed.
    cells.push({ date, day: n, open: open.has(date) && count !== 0, count })
  }
  while (cells.length % 7) cells.push(null)

  const weeks: CalendarWeek[] = []
  for (let r = 0; r < cells.length; r += 7) {
    const cellsOfWeek = cells.slice(r, r + 7)
    const anchor = cellsOfWeek.find(Boolean) as CalendarCell
    weeks.push({ kw: isoWeekNumber(dayjs(anchor.date)), cells: cellsOfWeek })
  }
  const bounds = calendarBounds(openDates, now)
  return {
    month: first.format("YYYY-MM-DD"),
    title: fmt(first, "MMMM YYYY"),
    weeks,
    canPrev: month > bounds.min,
    canNext: month < bounds.max,
    showLegend: cells.some((c) => c?.open && c.count !== null),
  }
}
